"use client"

import { useState, useRef, useCallback, useEffect } from "react"
import { Pen, Square, Undo2, Trash2, Check } from "lucide-react"
import { useTranslation } from "@/lib/i18n"
import { cn } from "@/lib/utils"

type Tool = "pen" | "rect"

interface Point {
  x: number
  y: number
}

interface Stroke {
  tool: Tool
  color: string
  points: Point[]
}

export function ScreenshotAnnotator({
  imageUrl,
  onDone,
  onCancel,
  fullscreen = false,
}: {
  imageUrl: string
  onDone: (annotatedImageUrl: string) => void
  onCancel: () => void
  fullscreen?: boolean
}) {
  const { t } = useTranslation()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [tool, setTool] = useState<Tool>("pen")
  const [color, setColor] = useState("#ef4444")
  const [strokes, setStrokes] = useState<Stroke[]>([])
  const [currentStroke, setCurrentStroke] = useState<Stroke | null>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const imageRef = useRef<HTMLImageElement | null>(null)
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 })

  // Load image
  useEffect(() => {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.onload = () => {
      imageRef.current = img
      if (fullscreen) {
        // Fit within viewport with padding (90vw x 80vh)
        const maxW = window.innerWidth * 0.88
        const maxH = window.innerHeight * 0.75
        const scaleW = maxW / img.width
        const scaleH = maxH / img.height
        const scale = Math.min(1, scaleW, scaleH)
        setCanvasSize({ width: img.width * scale, height: img.height * scale })
      } else {
        // Fit within container (max ~360px wide, proportional height)
        const maxW = 360
        const scale = Math.min(1, maxW / img.width)
        setCanvasSize({ width: img.width * scale, height: img.height * scale })
      }
      setImageLoaded(true)
    }
    img.src = imageUrl
  }, [imageUrl, fullscreen])

  const rafRef = useRef<number>(0)

  // Redraw canvas whenever strokes change
  const redraw = useCallback(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext("2d")
    if (!canvas || !ctx || !imageRef.current) return

    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height)

    const allStrokes = currentStroke ? [...strokes, currentStroke] : strokes

    for (const stroke of allStrokes) {
      ctx.strokeStyle = stroke.color
      ctx.lineWidth = 3
      ctx.lineCap = "round"
      ctx.lineJoin = "round"

      if (stroke.tool === "pen" && stroke.points.length > 1) {
        ctx.beginPath()
        ctx.moveTo(stroke.points[0].x, stroke.points[0].y)
        for (let i = 1; i < stroke.points.length; i++) {
          ctx.lineTo(stroke.points[i].x, stroke.points[i].y)
        }
        ctx.stroke()
      } else if (stroke.tool === "rect" && stroke.points.length >= 2) {
        const start = stroke.points[0]
        const end = stroke.points[stroke.points.length - 1]
        ctx.strokeRect(start.x, start.y, end.x - start.x, end.y - start.y)
      }
    }
  }, [strokes, currentStroke])

  useEffect(() => {
    cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(redraw)
    return () => cancelAnimationFrame(rafRef.current)
  }, [redraw])

  const getCanvasPoint = useCallback(
    (e: React.MouseEvent | React.TouchEvent): Point => {
      const canvas = canvasRef.current
      if (!canvas) return { x: 0, y: 0 }
      const rect = canvas.getBoundingClientRect()
      const touch = "touches" in e ? (e.touches[0] || e.changedTouches[0]) : null
      const clientX = touch ? touch.clientX : (e as React.MouseEvent).clientX
      const clientY = touch ? touch.clientY : (e as React.MouseEvent).clientY
      return {
        x: ((clientX - rect.left) / rect.width) * canvas.width,
        y: ((clientY - rect.top) / rect.height) * canvas.height,
      }
    },
    []
  )

  const handlePointerDown = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if ("touches" in e) e.preventDefault()
      const point = getCanvasPoint(e)
      setIsDrawing(true)
      setCurrentStroke({ tool, color, points: [point] })
    },
    [tool, color, getCanvasPoint]
  )

  const handlePointerMove = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!isDrawing || !currentStroke) return
      if ("touches" in e) e.preventDefault()
      const point = getCanvasPoint(e)
      setCurrentStroke((prev) =>
        prev ? { ...prev, points: [...prev.points, point] } : null
      )
    },
    [isDrawing, currentStroke, getCanvasPoint]
  )

  const handlePointerUp = useCallback(() => {
    if (currentStroke && currentStroke.points.length > 1) {
      setStrokes((prev) => [...prev, currentStroke])
    }
    setCurrentStroke(null)
    setIsDrawing(false)
  }, [currentStroke])

  const handleUndo = useCallback(() => {
    setStrokes((prev) => prev.slice(0, -1))
  }, [])

  const handleClear = useCallback(() => {
    setStrokes([])
  }, [])

  const handleDone = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    // Export at full resolution
    const exportCanvas = document.createElement("canvas")
    const img = imageRef.current
    if (!img) return
    exportCanvas.width = img.width
    exportCanvas.height = img.height
    const ctx = exportCanvas.getContext("2d")
    if (!ctx) return

    ctx.drawImage(img, 0, 0)

    const scaleX = img.width / canvas.width
    const scaleY = img.height / canvas.height

    for (const stroke of strokes) {
      ctx.strokeStyle = stroke.color
      ctx.lineWidth = 3 * scaleX
      ctx.lineCap = "round"
      ctx.lineJoin = "round"

      if (stroke.tool === "pen" && stroke.points.length > 1) {
        ctx.beginPath()
        ctx.moveTo(stroke.points[0].x * scaleX, stroke.points[0].y * scaleY)
        for (let i = 1; i < stroke.points.length; i++) {
          ctx.lineTo(stroke.points[i].x * scaleX, stroke.points[i].y * scaleY)
        }
        ctx.stroke()
      } else if (stroke.tool === "rect" && stroke.points.length >= 2) {
        const start = stroke.points[0]
        const end = stroke.points[stroke.points.length - 1]
        ctx.strokeRect(
          start.x * scaleX,
          start.y * scaleY,
          (end.x - start.x) * scaleX,
          (end.y - start.y) * scaleY
        )
      }
    }

    const dataUrl = exportCanvas.toDataURL("image/png", 0.8)
    onDone(dataUrl)
  }, [strokes, onDone])

  if (!imageLoaded) {
    return (
      <div className="flex items-center justify-center h-40 text-xs text-muted-foreground">
        {t("feedbackTickets.annotator.loading")}
      </div>
    )
  }

  const colors = ["#ef4444", "#f59e0b", "#22c55e", "#3b82f6", "#ffffff"]

  const iconSize = fullscreen ? "h-5 w-5" : "h-3.5 w-3.5"
  const btnPad = fullscreen ? "p-2.5" : "p-1.5"
  const swatchSize = fullscreen ? "h-7 w-7" : "h-5 w-5"

  return (
    <div ref={containerRef} className={cn("space-y-2", fullscreen && "flex flex-col items-center")}>
      {/* Toolbar — flex-wrap so colour swatches drop to a second row on narrow viewports */}
      <div className={cn("flex items-center gap-1.5 px-1 flex-wrap", fullscreen && "gap-2.5 bg-white/10 rounded-xl px-4 py-2")}>
        <button
          onClick={() => setTool("pen")}
          className={cn(
            "rounded-md transition-colors", btnPad,
            tool === "pen"
              ? fullscreen ? "bg-white text-black" : "bg-primary text-primary-foreground"
              : fullscreen ? "hover:bg-white/20 text-white" : "hover:bg-muted text-muted-foreground"
          )}
          title={t("feedbackTickets.annotator.pen")}
        >
          <Pen className={iconSize} />
        </button>
        <button
          onClick={() => setTool("rect")}
          className={cn(
            "rounded-md transition-colors", btnPad,
            tool === "rect"
              ? fullscreen ? "bg-white text-black" : "bg-primary text-primary-foreground"
              : fullscreen ? "hover:bg-white/20 text-white" : "hover:bg-muted text-muted-foreground"
          )}
          title={t("feedbackTickets.annotator.rect")}
        >
          <Square className={iconSize} />
        </button>

        <div className={cn("w-px bg-border mx-0.5", fullscreen ? "h-6 bg-white/20" : "h-4")} />

        {colors.map((c) => (
          <button
            key={c}
            onClick={() => setColor(c)}
            className={cn(
              "rounded-full border-2 transition-transform", swatchSize,
              color === c
                ? fullscreen ? "scale-110 border-white" : "scale-110 border-foreground"
                : "border-transparent hover:scale-105"
            )}
            style={{ backgroundColor: c }}
          />
        ))}

        <div className={cn("w-px bg-border mx-0.5", fullscreen ? "h-6 bg-white/20" : "h-4")} />

        <button
          onClick={handleUndo}
          disabled={strokes.length === 0}
          className={cn(
            "rounded-md disabled:opacity-30 transition-colors", btnPad,
            fullscreen ? "hover:bg-white/20 text-white" : "hover:bg-muted text-muted-foreground"
          )}
          title={t("common.undo")}
        >
          <Undo2 className={iconSize} />
        </button>
        <button
          onClick={handleClear}
          disabled={strokes.length === 0}
          className={cn(
            "rounded-md disabled:opacity-30 transition-colors", btnPad,
            fullscreen ? "hover:bg-white/20 text-white" : "hover:bg-muted text-muted-foreground"
          )}
          title={t("common.clearAll")}
        >
          <Trash2 className={iconSize} />
        </button>
      </div>

      {/* Canvas */}
      <canvas
        ref={canvasRef}
        width={canvasSize.width}
        height={canvasSize.height}
        className={cn(
          "rounded-lg border cursor-crosshair",
          fullscreen ? "shadow-2xl" : "w-full"
        )}
        style={fullscreen
          ? { width: canvasSize.width, height: canvasSize.height }
          : { maxHeight: 200 }
        }
        onMouseDown={handlePointerDown}
        onMouseMove={handlePointerMove}
        onMouseUp={handlePointerUp}
        onMouseLeave={handlePointerUp}
        onTouchStart={handlePointerDown}
        onTouchMove={handlePointerMove}
        onTouchEnd={handlePointerUp}
      />

      {/* Action buttons — min-w so both pills are equal width; no flex-1 (parent is flex-col in fullscreen) */}
      <div className={cn("flex items-center justify-center gap-2", fullscreen && "gap-3")}>
        <button
          onClick={handleDone}
          className={cn(
            "flex items-center justify-center gap-1.5 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors min-w-[7rem]",
            fullscreen ? "px-5 py-2.5 text-sm" : "px-4 py-1.5 text-xs"
          )}
        >
          <Check className={fullscreen ? "h-4 w-4" : "h-3 w-3"} /> {t("feedbackTickets.annotator.done")}
        </button>
        <button
          onClick={onCancel}
          className={cn(
            "flex items-center justify-center gap-1.5 rounded-lg border font-medium transition-colors min-w-[7rem]",
            fullscreen ? "px-5 py-2.5 text-sm text-white border-white/30 hover:bg-white/10" : "px-4 py-1.5 text-xs hover:bg-muted"
          )}
          aria-label={t("feedbackTickets.annotator.cancel")}
        >
          {t("common.cancel")}
        </button>
      </div>
    </div>
  )
}
